/**
 * The daily report — the one screen a foreman touches every single day.
 *
 * THE HIGHEST-VALUE SCREEN IN THE APPLICATION after the sign-in page, and the reason is not that it
 * has the most strings. It is the screen where a Spanish-speaking foreman decides whether his crew
 * gets paid for Tuesday: attendance, hours, equipment, materials, the receipts he photographed, and
 * the work he did. Getting `Faltó` wrong on this screen costs somebody a day's pay.
 *
 * ⚠ TWO SOURCES OF PROSE ON THIS SCREEN ARE NOT TRANSLATABLE FROM HERE, and pretending otherwise
 * would be worse than saying so:
 *
 *   1. THE VERIFICATION MESSAGES. `verification.errors[].message` and `.warnings[].message` are
 *      composed by the SERVER (timekeeping's verify endpoint) and interpolate row numbers and project
 *      names — "Materials line 2 has no project". There is no message code to switch on, so the
 *      exact-match table the QR page uses (modules/forms/i18n.js `SERVER_MESSAGES`, guarded by a
 *      pytest that AST-reads the server's literals) cannot be reproduced here without one. They print
 *      in English, beside a Spanish heading that says what they are.
 *   2. `e.response.data.detail` on a failed save, submit or delete. Same reason, same honest
 *      fallback: English a foreman can show his superintendent, never a blank and never a code.
 *
 * The durable fix for both is a stable `code` beside `detail` on the timekeeping API. It is not
 * built, and this note is what stands in for it.
 */
export default {
  loadingReport: 'Loading report...',
  title: 'Daily Report',
  modeEdit: 'Edit',
  modeNew: 'New',
  enteredBy: (name) => `(entered by ${name})`,

  // ── the header row ────────────────────────────────────────────────────────────────────────────
  date: 'Date',
  dateEditableOnRejected: 'Date can be changed on rejected reports',
  dateEditableOnRejectedNote: '✏️ Date is editable on rejected reports',
  present: 'Present',
  totalHours: 'Total Hours',
  projects: 'Projects',
  showAllProjects: 'Show all projects',
  showAllProjectsHelp: 'Bypass department filter to see all active projects',

  // ── entering a report for somebody else ───────────────────────────────────────────────────────
  creatingFor: 'Creating for:',
  me: (name) => `${name} (Me)`,
  selectForeman: 'Select foreman...',
  onBehalfOf: 'On Behalf Of',
  foreman: 'Foreman',

  // ── verification ──────────────────────────────────────────────────────────────────────────────
  verificationResults: 'Verification Results',
  completeRequiredForms: 'Complete these required forms, then submit again:',
  fillForm: (name) => `Fill “${name}”`,
  form: 'form',
  allChecksPassed: 'All checks passed — ready to submit',
  cannotSubmit: (n) => (n === 1
    ? 'Cannot submit — 1 problem above'
    : `Cannot submit — ${n} problems above`),

  // ── present, but no hours ─────────────────────────────────────────────────────────────────────
  presentNoHours: (n) => (n === 1
    ? '1 person is marked present with no hours'
    : `${n} people are marked present with no hours`),
  presentNoHoursHelp: 'Nothing will reach payroll for them. Enter their time below, or confirm you '
    + 'meant it — the confirmation is saved with the report.',
  workedNoHoursConfirmed: 'worked no hours — confirmed',
  workedNoHoursWhy: 'Why? (sent home, standby, travel…)',
  workedNoHoursReasonFor: (name) => `Reason ${name} worked no hours`,

  // ── forms attached to this report ─────────────────────────────────────────────────────────────
  formsOnThisReport: 'Forms on this report',
  backToDailyReport: 'Back to daily report',

  // ── crew & hours ──────────────────────────────────────────────────────────────────────────────
  crewAndHours: 'Crew & Hours',
  // THE CONTROL THAT DECIDES WHICH GRID A FOREMAN MEETS, sitting directly under the heading above
  // and on top of three grids that are translated. On a phone the words are hidden and only the
  // icon shows, so the `title` IS the label there — both are resolved here.
  hoursModeDetailed: 'Detailed',
  hoursModeDetailedHelp: 'Start/end times per project',
  hoursModeSplit: 'Split Hours',
  hoursModeSplitHelp: 'Hours per project',
  hoursModeGrid: 'Grid',
  hoursModeGridHelp: 'Spreadsheet view',
  rollCall: (working, absent) => `Roll Call — ${working} working / ${absent} absent`,
  borrowedEmployee: 'Borrowed Employee',
  pickEmployee: 'Pick employee...',
  attendancePresent: 'Present',
  attendanceAbsent: 'Absent',
  attendanceLateArrival: 'Late Arrival',
  attendanceEarlyOut: 'Early Out',
  attendanceBorrowed: 'Borrowed',
  reasonPrompt: 'reason?',
  reasonSick: 'Sick',
  reasonVacation: 'Vacation',
  reasonPersonal: 'Personal',
  reasonNoCallNoShow: 'NCNS',
  reasonInjury: 'Injury',
  reasonWeather: 'Weather',
  reasonOther: 'Other',
  reasonFreeText: 'Reason...',
  // WHAT THE ABSENT CARD PRINTS UNDER THE DROPDOWN, and it was interpolating the STORED VALUE:
  // `Reason: no_call_no_show`, a raw database key on the one string on this screen that reaches
  // payroll, directly beneath a dropdown reading the same fact in words. Never a key, in any
  // language — so the word is resolved from the ids above and only the sentence is built here.
  absenceReasonIs: (word) => `Reason: ${word}`,
  noHoursRecorded: 'No hours recorded',

  copyPreviousPrompt: 'Start faster by copying data from your previous report',
  copyPreviousDay: '📋 Copy Previous Day',
  importTimesheets: 'Import timesheets from Excel or PDF/Scans',
  uploadFiles: 'Upload Files',

  // ── the line-item sections ────────────────────────────────────────────────────────────────────
  //
  // ⚠ "New equipment" IS FOUR STRINGS, NOT ONE TEMPLATE. English builds it as
  // `New ${label.toLowerCase()}` and that works because English adjectives do not agree with
  // anything. Spanish needs `Nuevo equipo` but `Nueva descripción`, so a single template would get
  // one of the four wrong — and the wrong one is grammatically wrong on every render, not just on an
  // edge case. Same reason ../t.js insists that anything with a number in it is a function.
  newEquipment: 'New equipment',
  newMaterial: 'New material',
  newReceipt: 'New receipt',
  newWorkDescription: 'New work description',
  // The numbered form is safe to template: "Equipment 2" and "Equipo 2" have the same shape.
  numberedRow: (label, position) => `${label} ${position}`,
  equipment: 'Equipment',
  equipmentRow: 'Equipment',
  materials: 'Materials',
  materialRow: 'Material',
  receipts: 'Receipts',
  receiptRow: 'Receipt',
  workDescriptions: 'Work Descriptions',
  workDescriptionRow: 'Work description',
  project: 'Project',
  selectProject: 'Select project...',
  selectEquipment: 'Select equipment...',
  equipmentName: 'Equipment name',
  customEntry: '— Custom entry —',
  enterCustomEquipment: 'Enter custom equipment name',
  unitNumber: 'Unit #',
  unit: 'Unit',
  unitHour: 'Hour',
  unitDay: 'Day',
  unitWeek: 'Week',
  // THE HOURS THE MACHINE WAS ON SITE (2.104.0), whatever the unit: the client's ticket prints this
  // column as "Hours", and for an Hour line it is what the rate is multiplied by. It was "Quantity",
  // so a line entered under the old label may hold a day count.
  equipmentHours: 'Hours',
  // Offered only when the company states a standby share (Fleet > Machine Types). C2.
  equipmentStandby: (pct) => `On standby (${pct}% of the rate)`,
  equipmentStandbyNotSet: 'On standby - your company no longer states a standby share, so this line will not be priced',
  // A unit the box no longer offers, shown (and sent) as it was saved rather than swapped for another.
  // Week is never a choice on a daily report; the company's weekly choice decides what a day costs.
  unitNoLongerUsed: (unit) => `${unit} (no longer used)`,
  // Said only of a Day on a machine whose card has no day rate here — it gets no price.
  unitNoPriceHere: (unit) => `${unit} (no price on this job)`,
  unitNotOffered: (unit) => `${unit} (not offered for this machine)`,
  // A stored Hour on a machine priced by the day and not by the hour: the server bills it by the day.
  unitChargedByTheDay: (unit) => `${unit} (charged by the day)`,
  // The grey lines under a machine row. No figures, ever: a foreman sees the same words as the office.
  hintNoPrice: 'No price for this machine on this job - its line will be flagged',
  hintUnitsUnknown: 'Could not check which units this machine is priced in',
  hintGoverned: "Charged by your company's weekly choice",
  hintNeedsUnitNumber: "Unit # needed: this machine's charge is counted by the week - use the same Unit # every day (no number? type a name, e.g. Rental 1)",
  hintSplitNoUnitNumber: 'With no Unit # this machine is charged a full day on this job, not split with its other jobs today',
  // WHICH MACHINE (PLAN-30 P2): the actual Fleet machine of the line's type. Optional - blank is fine,
  // and billing stays by machine type either way. Labels only, never a figure.
  whichMachine: 'Which machine?',
  machineNotPicked: 'Not picked',
  machineNoneOfType: 'No machines of this type in Fleet - leave it blank',
  machineNotLinked: 'Not linked to a Fleet machine. Pick the machine in "Which machine?" - until then its job cost cannot come from the machine.',
  machineNotLinkedTyped: (typed) => `Not linked to a Fleet machine: ${typed} is not one of your machines of this type. Pick the machine in "Which machine?" and leave the Unit # as it is - billing counts the machine by it.`,
  machineListFailed: "Could not load this type's machines - leave it blank or try again",
  // Rented machines (PLAN-30 P3).
  machineRentedNoCost: "Rented: its cost is the vendor's invoice, so these hours carry no cost.",
  machineNewRental: 'Machine not listed? New rental',
  machineNewRentalTitle: 'New rental',
  machineNoLongerOffered: (name) => `${name} (no longer offered)`,
  machineOnReport: 'Machine',
  machineStatus_out_of_service: 'out of service',
  machineStatus_under_repair: 'under repair',
  machineStatus_inactive: 'inactive',
  machineOwnership_leased: 'leased',
  machineOwnership_rented: 'rented',
  machineOwnership_subcontracted: 'subcontracted',
  // A machine picked that could not be kept. The SAVE WENT THROUGH - a machine never holds up anybody's
  // pay - and the line was saved with no machine. Said on the row.
  machineLeftBlank_sold: (n) => `Machine ${n} is sold, so it was left blank. Pick another or leave it blank.`,
  machineLeftBlank_retired: (n) => `Machine ${n} is retired, so it was left blank. Pick another or leave it blank.`,
  machineLeftBlank_deleted: (n) => `Machine ${n} has been deleted from Fleet, so it was left blank. Pick another or leave it blank.`,
  machineLeftBlank_other_type: (n) => `Machine ${n} is a different machine type in Fleet, so it was left blank. Pick another or leave it blank.`,
  machineLeftBlank_type_changed: (n) => `The machine type changed, so machine ${n} was left blank. Pick another or leave it blank.`,
  machineLeftBlank_not_yours: () => "That machine is not one of your company's Fleet machines, so it was left blank. Pick another or leave it blank.",
  machineLeftBlank_no_type: () => 'A machine can be picked only for a machine type from the list, so it was left blank.',
  machineLeftBlank_fleet_unreadable: () => "Fleet's machines could not be read, so the machine was left blank. Pick it again or leave it blank.",
  machinesLeftBlank: (n) => (n === 1 ? '1 machine was left blank - see the Equipment rows.'
    : `${n} machines were left blank - see the Equipment rows.`),
  // The picked machine's number and the Unit # box disagree. Billing (and the day split) use the Unit #
  // box, so the screen says so and offers the picked number in one tap - never filled by itself.
  machineUnitNumberDiffers: (typed, num) => `Billing uses the Unit # box (${typed || 'blank'}). Use ${num}?`,
  machineUnitNumberKept: (typed) => `Billing uses the Unit # box (${typed || 'blank'}), the number this machine already has on this job this week - keep it.`,
  machineUnitNumberBilled: (typed) => `Billing uses the Unit # box (${typed || 'blank'}).`,
  useMachineNumber: (num) => `Use ${num}`,
  addEquipment: 'Add Equipment',
  description: 'Description',
  materialDescription: 'Material description',
  amount: 'Amount ($)',
  addMaterial: 'Add Material',

  // ── entering the hours: the three modes of the Crew & Hours section ───────────────────────────
  //
  // ONE DICTIONARY FOR ALL THREE, because they are one screen. `bfs_time_entry_mode` is remembered
  // per browser, so which of GroupedTimeBlocksEntry / SplitHoursEntry / HoursGridEntry a foreman
  // meets is whichever he last chose — and translating only the default would leave a third of the
  // crews reading an English hours grid under a Spanish heading. A screen that is half Spanish reads
  // as broken.
  //
  // The attendance words themselves are NOT repeated here: both the chip bar and the detailed grid
  // resolve `attendancePresent` … `attendanceBorrowed` above, so the word that decides whether a man
  // is paid cannot differ between two entry modes of the same section.
  disputed: 'Disputed',
  borrowedTag: 'BORROWED',
  start: 'Start',
  end: 'End',
  lunch: 'Lunch',
  hours: 'Hours',
  addProjectLine: 'Add Project Line',
  removeProjectLine: 'Remove project line',
  noCrewLoaded: 'No crew members loaded. Crew data may still be loading.',
  borrowEmployee: '+ Borrow Employee',
  removeBorrowedEmployee: 'Remove borrowed employee',
  noEmployeesToBorrow: 'No employees available to borrow.',
  searchEmployeesToBorrow: 'Search employees to borrow…',

  // split mode
  noSplitsYet: 'No employees have split-hours entries yet.',
  initializeAllPresent: 'Initialize All Present Employees',
  initializeAllPresentShort: '👥 Initialize All Present',
  total: 'Total:',
  addProject: 'Add Project',
  remaining: 'Remaining:',
  removeSplit: 'Remove split',
  applyToOthers: 'Apply to Others',
  copySplitsTo: 'Copy splits to:',
  copySplitsToOthers: 'Copy splits to other employees',
  apply: 'Apply',

  // grid mode
  gridEntry: 'Grid Entry',
  noEmployeesPresent: 'No employees present — add employees to the daily report first',
  noEmployeesToDisplay: 'No employees to display. Add employees to the daily report first.',
  employee: 'Employee',
  classificationShort: 'Class.',
  totalPlain: 'Total',
  removeProjectColumn: 'Remove project column',
  fillDown: 'Fill down — copy first value to all rows',
  lunchAll: 'Lunch (all):',
  startTime: 'Start time:',
  searchProject: 'Search project...',

  // ── receipts ──────────────────────────────────────────────────────────────────────────────────
  receiptsHelp: 'Photograph each receipt as you get it. They go to the office for approval when you '
    + 'submit this report — nothing is charged to the job until Finance approves it.',
  uploading: 'Uploading…',
  retakePhoto: 'Retake photo',
  takePhoto: 'Take photo',
  chooseAnother: 'Choose another',
  chooseExisting: 'Choose existing',
  takeReceiptPhoto: 'Take receipt photo',
  receiptPhoto: 'Receipt photo',
  // Names the button that is ACTUALLY on screen. The English used to say `Tap "Replace photo"` beside
  // a button reading "Retake photo" — survivable when you can read both, useless when you are
  // matching shapes in a language you are still learning to navigate.
  photoRetryHint: (button) => `Tap “${button}” to try again.`,
  job: 'Job',
  selectJob: 'Select job...',
  vendor: 'Vendor',
  whereYouBoughtIt: 'Where you bought it',
  category: 'Category',
  selectCategory: 'Select…',
  notes: 'Notes',
  whatItWasFor: 'What it was for (optional)',
  removeReceipt: 'Remove receipt',
  addReceipt: 'Add Receipt',
  catMaterials: 'Materials',
  catEquipment: 'Equipment / rental',
  catFuel: 'Fuel',
  catTools: 'Tools',
  catSubcontractor: 'Subcontractor',
  catMeals: 'Meals',
  catTravel: 'Travel / lodging',
  catOther: 'Other',

  // What FINANCE holds for a receipt row, in the foreman's words.
  financePending: 'Sent to Finance — awaiting approval.',
  financeProcessed: 'Approved by Finance.',
  financeRejected: 'Finance rejected this receipt.',
  financeRejectedTodo: 'Fix it and send the report again, or remove the row.',
  financeRemoved: 'The office removed this receipt, so it is no longer with Finance.',
  financeRemovedTodo: 'Send the report again to re-send it, or remove the row.',
  financeUnknown: (status) => `With Finance — ${status}.`,

  // ── work descriptions ─────────────────────────────────────────────────────────────────────────
  workPerformed: 'Work Performed',
  describeWork: 'Describe work performed...',
  addWorkDescription: 'Add Work Description',

  // ── field tickets ─────────────────────────────────────────────────────────────────────────────
  generatedFieldTickets: 'Generated Field Tickets',
  hoursLabor: (hours) => `${hours}h labor`,

  // ── the action bar ────────────────────────────────────────────────────────────────────────────
  cancel: 'Cancel',
  deleteDraft: 'Delete Draft',
  receiptStillUploading: 'a receipt photo is still uploading',
  saving: 'Saving...',
  saveDraft: 'Save Draft',
  verify: 'Verify',
  submitting: 'Submitting...',
  submit: 'Submit',
  back: 'Back',
  requestAmendment: 'Request Amendment',
  deleteReport: 'Delete Report',

  // ── deleting a submitted report ───────────────────────────────────────────────────────────────
  deleteSubmittedTitle: 'Delete Submitted Report',
  deleteSubmittedBody: (status) => `This report has been ${status}. Deleting it is permanent and `
    + 'cannot be undone. Please provide a reason (minimum 10 characters).',
  deleteReasonPlaceholder: 'Reason for deletion...',
  deleteReasonCount: (n) => `${n}/10 characters minimum`,
  permanentlyDelete: 'Permanently Delete',
  confirmDeleteDraft: 'Delete this draft? This cannot be undone.',
  confirmAmendment: 'Create an amendment? This will create a new editable draft copy of this approved '
    + 'report. The original will be preserved for audit purposes.',

  // ── what the screen says back ─────────────────────────────────────────────────────────────────
  loadedExistingDraft: 'Loaded existing draft for today',
  loadedPrevious: (date) => `Loaded data from ${date}. Review and adjust as needed.`,
  noPreviousReport: 'No previous report found to copy from.',
  draftSaved: 'Draft saved',
  reportSubmitted: 'Report submitted for approval!',
  amendmentCreated: 'Amendment created! Redirecting to the new draft...',
  notReadyToSubmit: 'Not ready to submit — fix the items below. The draft is saved.',
  cannotSubmitFixErrors: 'Cannot submit — fix errors below',
  reasonTooShort: 'Reason must be at least 10 characters',
  disputeResolvedReverted: 'Dispute resolved — hours changed, report sent back for re-review',
  disputeResolvedNotified: 'Dispute resolved — employee notified',
  failedToLoadReport: 'Failed to load report',
  failedToLoadPrevious: 'Failed to load previous report',
  failedToSave: 'Failed to save',
  failedToDeleteDraft: 'Failed to delete draft',
  failedToDeleteReport: 'Failed to delete report',
  failedToCreateAmendment: 'Failed to create amendment',
  failedToResolveDispute: 'Failed to resolve dispute',

  // ── what did we finish today (WorkCompletedStep) ──────────────────────────────────────────────
  //
  // Every sentence here is read on a phone, outdoors, by somebody with five minutes for the whole
  // report. The save messages are the ones that matter most: they are the only way a foreman knows
  // whether the office has his work, so each one says what happened AND what happens next.
  wcTitle: 'What did we finish today',
  wcLoading: 'Loading what this job expects…',
  wcPickJobs: 'Pick the jobs your crew worked on above, and what you finished on each will appear here.',
  wcFromEstimate: 'From the estimate — enter what you finished',
  wcFromSchedule: 'From the schedule of rates — enter what was installed',
  wcRequired: 'Required by your company',
  wcNothingInstalled: 'Nothing installed on this job today',
  wcFindLine: 'Find a line',
  wcNoLineMatches: 'No line matches that.',
  wcOtherLines: 'Other lines',
  wcSoFar: (done, estimate, unit) => `${done} of ${estimate} ${unit} reported so far`,
  // Progress on long items / attach at completion (closed loop P3b).
  wcPctSoFar: (pct) => `${pct}% done before today - give the % done so far`,
  wcPctOf: (what) => `% done so far of ${what}`,
  wcPctBelow: (pct) => `Less than the ${pct}% already reported - a % done never goes backwards.`,
  wcPctAbove: (pct) => `More than the ${pct}% a later day already reports - correct that day first.`,
  wcPctOver: 'A line cannot be more than 100% done - type the % done so far, 100 at most.',
  wcPctMoveHint: 'If the crew worked on it today, move the % - even a little. A day it does not move, its hours cannot be counted toward it.',
  wcInstalledHowMany: (unit) => `Finished - how many ${unit} were installed?`,
  wcInstalledOf: (what) => `How many were installed of ${what}`,
  wcProgressHint: 'Lines that take more than a day ask for the % done so far instead of a count.',
  wcHeldHint: 'Your company attaches hours when work is finished: tick an item only on the day it is finished.',
  // NIT-G: both devices changed one line, and this phone's change was kept.
  wcReplacedElsewhere: (what) => `Your change to ${what} replaced one made on another device.`,
  wcNoLongerListed: 'No longer on the estimate',
  wcOneLess: (what) => `One less ${what}`,
  wcOneMore: (what) => `One more ${what}`,
  wcHowManyOf: (what) => `How many ${what}`,
  wcHowMany: 'How many',
  wcHowManyFinished: 'How many finished',
  wcTypeTheNumber: 'Type the number',
  wcAddCount: 'Add a count',
  // C3 item 6: rework - redoing work already finished. Its hours count as rework; it adds nothing finished.
  wcAddRework: 'Rework: redoing finished work',
  wcReworkTitle: 'Rework today',
  wcReworkHint: 'Pick the finished work the crew redid. It adds nothing finished; its hours count as rework.',
  wcReworkPick: 'Which finished work was redone',
  wcReworkPickOne: 'Pick the work…',
  wcReworkHowMany: 'How many redone',
  wcReworkWhy: 'Why (optional)',
  wcReworkAdd: 'Add the rework',
  wcReworkCancel: 'Cancel',
  wcReworkNone: 'Nothing finished on this job yet to redo.',
  wcReworkLoadFailed: 'Could not load the finished work. Try again.',
  wcReworkRemove: 'Remove this rework',
  wcReworkRedoing: (what, n) => `Redoing ${what} × ${n}`,
  wcWeld: 'Weld',
  wcRemoveGroup: 'Remove this group',
  wcOptional: '(optional)',
  wcNotInList: 'Not in the list…',
  wcSayWhatItIs: 'Say what it is',
  wcKeptAsTyped: 'Kept exactly as typed, for the office to see.',
  wcOffHoursTitle: 'Not on today’s hours',
  wcOffHoursBody: (job) => `There are no hours on ${job} on this report, so these are not sent. `
    + 'Put the hours back, or remove them.',
  wcRemove: 'Remove',
  wcSaving: 'Saving…',
  wcSaved: 'Saved.',
  wcSavedKeptWords: (n) => (n === 1
    ? 'Saved. 1 word is kept for the office to look at.'
    : `Saved. ${n} words are kept for the office to look at.`),
  wcNotSentYet: 'Not sent yet — kept on this phone.',
  wcKeptUntilSaved: 'Not sent yet — kept on this phone. It goes when you save the report.',
  wcNoSignal: 'Not sent yet — kept on this phone, and it will go when you are back on signal.',
  wcRefused: (why) => `The office’s system did not accept this: ${why} It is kept on this phone — `
    + 'ask the office.',
  // No longer "what you type here is kept": that invited him to retype a line that was already
  // saved, and the retype was counted twice. Until the load succeeds the lines cannot be typed on.
  wcCouldNotLoad: 'Could not load what was already saved for this report. It loads when you have '
    + 'signal — until then the lines cannot be changed, and a count you add is kept on this phone.',
  wcLinesWaitForLoad: 'These lines open once what was already saved has loaded, so nothing is '
    + 'counted twice.',
  wcListNotLoaded: 'The list for this job could not be loaded — it will load when you have signal.',
  wcTryAgain: 'Try again',
  wcEnteredEarlier: 'Entered earlier — the rest of the list shows when it loads',
  // UI-3: a company that switched installed quantities to "Not asked" on unit-price jobs.
  wcNotAskedAnyMore: 'Entered before your company stopped asking for this',
  wcNotAskedHere: 'Your company does not ask what was installed on these jobs.',
  wcServerTrouble: 'Not sent yet — the office’s system had a problem. It is kept on this phone and '
    + 'will be tried again.',
  wcPractice: 'Practice run — nothing here is sent.',
  wcDidNotReachOffice: 'Some of what was typed on this phone did not reach the office before this '
    + 'report was sent.',
  // Rows he left on a job with no hours (the "Not on today's hours" card). Deliberate, so not alarming.
  wcLeftOffReport: 'Some counts kept on this phone were for jobs with no hours on this report, so '
    + 'they were left off it.',
  // The work vocabulary (weld, its pickers and their choices). The server seeds these words in
  // English, keyed ("weld", "weld.material", "weld.material.carbon_steel"). In English the company's
  // own label is shown — a company that renamed "Carbon steel" to "CS" sees "CS". Other languages
  // translate the seeded keys and fall back to that label for a word a company added itself.
  wcVocab: (id, label) => label || id,
  // Said by the page, not the step: the one refusal that stops a submit.
  wcNotReachedOfficeYet: 'What you finished has not reached the office yet — check your signal and '
    + 'try again.',
  // ── the hours' cost code and the code split (closed loop Phase 3, F1 / F8) ────────────────────
  // A suggestion is never an answer: the field starts "Not said" and only a tap on Use says it.
  ccLabel: 'Cost code',
  ccNotSaid: 'Not said',
  ccHint: 'Capital job. Saying which part of the job these hours went to is what lets the estimate be '
    + 'checked against what the work actually took.',
  ccLastReport: (code) => `Last report: ${code}`,
  ccUse: 'Use',
  csTicked: (off, hours) => `You ticked work on ${off} but booked hours only to ${hours} - this day can't be `
    + "split by code, so none of its hours would reach the work. Give each person's hours their code below.",
  csNoCode: 'no code',
  csBlockCode: (who, from, to) => `${who} ${from}–${to} cost code`,
  csPersonCode: (who) => `${who} cost code`,
  csOnThisJob: (who, hours) => `${who} · ${hours} h on this job`,
  csAnotherCode: '+ hours on another code',
  csExtraHours: (who, n) => `${who} hours on another code, line ${n}`,
  csExtraCode: (who, n) => `${who} other cost code, line ${n}`,
  csHoursOn: 'h on',
  csRemove: 'remove',
  csSomeone: 'Someone',
  csGoThere: (job) => `Your ticks on ${job} need the hours split by code - give each person's hours their code`,
  // F7 / F5 on the finished-work step.
  wcNothingFinished: 'Nothing finished on this job today',
  wcLastTime: (what) => `Last time: ${what}`,
  wcUse: 'Use',
}
